import { useUniSelectorContext } from '@src/contexts/uni-selector/useUniSelectorContext';
import { useEmbeddedAuth } from '@src/contexts/embedded-auth/useEmbeddedAuth';
import { BASE_API_URL } from '@src/config';

export { BASE_API_URL };

export interface FetchOptions extends RequestInit {
  skipAuth?: boolean;
}

export function useApi() {
  const { getAccessToken, logout } = useEmbeddedAuth();

  const { selectedUniversityId } = useUniSelectorContext();
  const BASE_URL = BASE_API_URL;

  const fetchWrapper = async (url: string, options: FetchOptions = {}) => {
    let accessToken;
    let headers = {};
    const scifreeUser = localStorage.getItem('scifreeUser');

    if (!options.skipAuth) {
      try {
        accessToken = await getAccessToken();
      } catch (error) {
        console.error('Error getting access token', error);
        logout();
        return;
      }
      if (accessToken) {
        headers = {
          Authorization: `Bearer ${accessToken}`,
        };
      }
      if (scifreeUser) {
        headers = {
          ...headers,
          'X-SciFree-User': scifreeUser,
        };
      }
      if (selectedUniversityId) {
        headers = {
          ...headers,
          'X-SciFree-Context': selectedUniversityId,
        };
      }
    }

    const response = await fetch(`${BASE_URL}${url}`, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });
    if (!response.ok) {
      if (response.status === 401) {
        localStorage.clear();
        logout();
      }
      const responseBody = await response.json();
      throw new FetchError(response, responseBody);
    }
    return response;
  };

  const get = async (url: string, options: FetchOptions = {}) => {
    return fetchWrapper(url, { ...options, method: 'GET' });
  };

  const post = async (
    url: string,
    body: unknown = {},
    options: FetchOptions = {}
  ) => {
    const isFormData = body instanceof FormData;
    const headers = isFormData
      ? { ...options.headers }
      : {
          'Content-Type': 'application/json',
          ...options.headers,
        };

    return fetchWrapper(url, {
      ...options,
      method: 'POST',
      headers,
      body: !isFormData ? JSON.stringify(body) : body,
    });
  };

  const put = async (
    url: string,
    body: unknown = {},
    options: FetchOptions = {}
  ) => {
    const isFormData = body instanceof FormData;
    const headers = isFormData
      ? { ...options.headers }
      : {
          'Content-Type': 'application/json',
          ...options.headers,
        };
    return fetchWrapper(url, {
      ...options,
      method: 'PUT',
      headers,
      body: !isFormData ? JSON.stringify(body) : body,
    });
  };

  const del = async (url: string, options: FetchOptions = {}) => {
    return fetchWrapper(url, { ...options, method: 'DELETE' });
  };

  const patch = async (
    url: string,
    body: unknown = {},
    options: FetchOptions = {}
  ) => {
    const isFormData = body instanceof FormData;
    const headers = isFormData
      ? { ...options.headers }
      : {
          'Content-Type': 'application/json',
          ...options.headers,
        };

    return fetchWrapper(url, {
      ...options,
      method: 'PATCH',
      headers,
      body: !isFormData ? JSON.stringify(body) : body,
    });
  };

  return { get, post, put, patch, del, fetch: fetchWrapper };
}

export class FetchError extends Error {
  constructor(
    public response: Response,
    public body: { message?: string | string[]; [key: string]: unknown },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...params: any[]
  ) {
    super(...params);
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, FetchError);
    }
  }
}

export function getErrorMessage(message?: string | string[]): string | null {
  if (!message) return null;
  return Array.isArray(message) ? message.join(', ') : message;
}
